/* =====================================================================
 * DMAgentList — 왼쪽 에이전트 + 팀 연락처 목록
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ChevronUp, ChevronDown, PanelLeft, Menu } from 'lucide-react';
import { DM_APP_AGENT, type DMAgentType, type Conversation } from '@auw/workspace-utils/types/dm';
import { useDMStore } from './hooks/useDMStore';
import { useDMConversationStore } from '@auw/stores';
import { useTeamStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { useChatStore } from '@auw/stores';
import { useFeatureFlags } from '@auw/feature-flags';
import DMTeamContactList from './DMTeamContactList';

interface Props {
  onSettingsClick: () => void;
  onSelectConversation: (conv: Conversation) => void;
  onSelectAgent?: (agentType: DMAgentType) => void;
  onConversationOpened?: (convId: string) => void;
  activeConversationId: string | null;
  pageSidebarOpen: boolean;
  onTogglePageSidebar: () => void;
  showPageSidebarToggle?: boolean;
  showWorkspaceSidebarToggle?: boolean;
  titleText?: string;
  flatStyle?: boolean;
  surfaceOpacity?: number;
}

const DMAgentList: React.FC<Props> = ({
  onSelectConversation,
  onSelectAgent,
  onConversationOpened,
  activeConversationId,
  pageSidebarOpen,
  onTogglePageSidebar,
  showPageSidebarToggle = true,
  showWorkspaceSidebarToggle = true,
  titleText,
  flatStyle = false,
  surfaceOpacity = 1,
}) => {
  const { t } = useTranslation('common');
  const resolvedTitleText = titleText ?? t('dm.directMessage');
  const {
    activeAgent,
    setActiveAgent,
    messages,
    unreadCounts,
    typingAgents,
    wsConnected,
  } = useDMStore();

  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setSidebarOpen = useChatStore((s) => s.setSidebarOpen);

  const { conversations, loadConversations, openDirect, setActiveConversation } =
    useDMConversationStore();
  const { teams, loadMyTeams } = useTeamStore();
  const currentUserId = useUserStore((s) => s.user?.userId || s.user?.email || '');
  /* P11-5h: when `mode.message.groupThread` is off, the list omits any
   * group conversations so 1:1-only tenants never see them even if backend
   * has lingering groups. The system_app channel respects its own flag. */
  const featureFlags = useFeatureFlags();
  const groupThreadEnabled = featureFlags.isEnabled('mode.message.groupThread');
  const systemAppChannelEnabled = featureFlags.isEnabled('mode.message.systemAppChannel');
  const [appOpen, setAppOpen] = useState(true);
  const [teamSections, setTeamSections] = useState<Record<string, boolean>>({});
  const [conversationAvatarErrorIds, setConversationAvatarErrorIds] = useState<Record<string, boolean>>({});

  // 초기 데이터 로드
  useEffect(() => {
    loadMyTeams();
    loadConversations();
  }, [loadMyTeams, loadConversations]);

  // 팀 멤버 맵 구성: team_id → members[]
  const teamMembersMap = useMemo(() => {
    const map: Record<string, { user_id: string; name: string; email: string; role: string }[]> = {};
    for (const team of teams) {
      // 팀 상세의 멤버는 currentDetail에 있지만, 여기서는 conversations에서 추론
      // 간단히 팀 ID만 기록 — 실제 멤버는 conversations의 members에서 가져옴
      map[team.team_id] = [];
    }
    return map;
  }, [teams]);

  // 대화를 팀별로 그룹핑
  const conversationsByTeam = useMemo(() => {
    const map: Record<string, Conversation[]> = {};
    for (const conv of conversations) {
      if (!groupThreadEnabled && conv.type === 'group') continue;
      if (!map[conv.team_id]) map[conv.team_id] = [];
      map[conv.team_id].push(conv);
    }
    return map;
  }, [conversations, groupThreadEnabled]);

  const toggleTeamSection = (teamId: string) => {
    setTeamSections((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  const handleAgentClick = (agentType: DMAgentType) => {
    setActiveAgent(agentType);
    setActiveConversation(null); // 에이전트 선택 시 대화 해제
    onSelectAgent?.(agentType);
  };

  const handleConversationClick = (conv: Conversation) => {
    setActiveConversation(conv.conversation_id);
    useDMStore.getState().setActiveAgent(null); // 에이전트 해제
    onSelectConversation(conv);
  };

  const markConversationAvatarError = (avatarKey: string) => {
    setConversationAvatarErrorIds((prev) => {
      if (prev[avatarKey]) return prev;
      return { ...prev, [avatarKey]: true };
    });
  };

  return (
    <Container $flatStyle={flatStyle} $surfaceOpacity={surfaceOpacity}>
      <Header>
        <HeaderLeft>
          {showWorkspaceSidebarToggle && !sidebarOpen && (
            <SidebarToggleBtn onClick={() => setSidebarOpen(true)} title={t('dm.expandMainSidePanel')}>
              <PanelLeft size={16} />
            </SidebarToggleBtn>
          )}
          <Title>{resolvedTitleText}</Title>
        </HeaderLeft>
        <HeaderRight>
          {showPageSidebarToggle && (
            <SidebarToggleBtn
              type="button"
              onClick={onTogglePageSidebar}
              title={pageSidebarOpen ? t('dm.collapsePageSidebar') : t('dm.expandPageSidebar')}
            >
              <Menu size={16} />
            </SidebarToggleBtn>
          )}
        </HeaderRight>
      </Header>

      <List>
        {/* ---- 앱 섹션 ---- */}
        {systemAppChannelEnabled && (
          <>
            <SectionHeader onClick={() => setAppOpen(!appOpen)}>
              <SectionTitle>{t('dm.app')}</SectionTitle>
              {appOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </SectionHeader>
            {appOpen && (
              <AppItem
                $active={activeAgent === 'system_app' && !activeConversationId}
                onClick={() => handleAgentClick('system_app')}
              >
                <AppLogoWrap>
                  <AppAvatarImg
                    src="/agent_logo.png"
                    alt={DM_APP_AGENT.name}
                  />
                </AppLogoWrap>
                <AppItemInfo>
                  <AppItemName>{DM_APP_AGENT.name}</AppItemName>
                  <AppItemDesc>
                    {(() => {
                      const appMessages = messages['system_app'];
                      const lastMsg = appMessages?.length > 0 ? appMessages[appMessages.length - 1] : undefined;
                      return lastMsg ? lastMsg.content.slice(0, 30) : t('dm.systemNotifications');
                    })()}
                  </AppItemDesc>
                </AppItemInfo>
                {(unreadCounts['system_app'] ?? 0) > 0 && (
                  <UnreadBadge>{unreadCounts['system_app'] > 99 ? '99+' : unreadCounts['system_app']}</UnreadBadge>
                )}
              </AppItem>
            )}
          </>
        )}

        {/* ---- 팀별 대화 섹션 (대화가 있는 팀만 표시) ---- */}
        {teams.map((team) => {
          const isOpen = teamSections[team.team_id] !== false; // default open
          const teamConvs = conversationsByTeam[team.team_id] || [];

          // 대화가 없으면 팀 섹션 자체를 숨김
          if (teamConvs.length === 0) return null;

          return (
            <React.Fragment key={team.team_id}>
              <SectionHeader onClick={() => toggleTeamSection(team.team_id)}>
                <SectionTitle>{team.name}</SectionTitle>
                {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </SectionHeader>
              {isOpen && (
                <>
                  {teamConvs.map((conv) => {
                    const isActive = activeConversationId === conv.conversation_id;
                    // 1:1 대화에서 상대 이름 추출
                    let displayName = conv.name || t('dm.newConversation');
                    let letter = 'D';
                    let color = '#10a37f';
                    let avatarPicture: string | null = null;

                    if (conv.type === 'group') {
                      displayName = conv.name || t('dm.groupChat');
                      letter = 'G';
                      color = '#6366F1';
                    } else if (conv.members && conv.members.length > 0) {
                      const other = conv.members.find((m) => m.user_id !== currentUserId);
                      if (other) {
                        const otherName = other.name
                          || (other.email ? other.email.split('@')[0] : null)
                          || (other.user_id.includes('@') ? other.user_id.split('@')[0] : other.user_id.slice(0, 8));
                        displayName = otherName;
                        letter = otherName.charAt(0).toUpperCase();
                        avatarPicture = other.picture ?? null;
                      }
                    }
                    const avatarKey = `conv:${conv.conversation_id}:${avatarPicture || 'none'}`;

                    return (
                      <ContactItem
                        key={conv.conversation_id}
                        $active={isActive}
                        onClick={() => handleConversationClick(conv)}
                      >
                        <ContactAvatar $color={color}>
                          {avatarPicture && !conversationAvatarErrorIds[avatarKey] ? (
                            <ContactAvatarImg
                              src={avatarPicture}
                              alt={displayName}
                              onError={() => markConversationAvatarError(avatarKey)}
                            />
                          ) : (
                            letter
                          )}
                        </ContactAvatar>
                        <ContactInfo>
                          <ContactName>{displayName}</ContactName>
                          {conv.last_message_preview && (
                            <ContactPreview>{conv.last_message_preview}</ContactPreview>
                          )}
                        </ContactInfo>
                        {conv.unread_count > 0 && (
                          <UnreadBadge>{conv.unread_count > 99 ? '99+' : conv.unread_count}</UnreadBadge>
                        )}
                      </ContactItem>
                    );
                  })}
                  {/* 대화 없을 때 빈 상태 — 팀 멤버 연락처에서 시작 가능 */}
                </>
              )}
            </React.Fragment>
          );
        })}

        {/* ---- 팀 멤버 연락처 (새 대화 시작) ---- */}
        {teams.length > 0 && (
          <DMTeamContactList
            flatStyle={flatStyle}
            surfaceOpacity={surfaceOpacity}
            onConversationOpen={(convId: string) => {
              // 대화가 열리면 목록 리프레시
              loadConversations();
              onConversationOpened?.(convId);
            }}
          />
        )}
      </List>
    </Container>
  );
};

export default DMAgentList;

/* ---- Styled ---- */

const Container = styled.div<{ $flatStyle: boolean; $surfaceOpacity: number }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-right: ${({ $flatStyle, theme }) =>
    $flatStyle
      ? 'none'
      : `1px solid ${theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(23,23,23,0.82)'
      : 'rgba(249,249,249,0.78)'};
  ${({ $flatStyle, $surfaceOpacity, theme }) => $flatStyle && `
    background: transparent;
  `}
  backdrop-filter: ${({ $flatStyle }) => ($flatStyle ? 'none' : 'blur(24px) saturate(180%)')};
  -webkit-backdrop-filter: ${({ $flatStyle }) => ($flatStyle ? 'none' : 'blur(24px) saturate(180%)')};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  padding: ${({ theme }) => theme.sidebar.headerPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: ${({ theme }) => theme.sidebarMobile.headerHeight};
    padding: ${({ theme }) => theme.sidebarMobile.headerPad};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SidebarToggleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(33,33,33,0.72)' : 'rgba(255,255,255,0.76)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 18px rgba(0,0,0,0.24)'
      : '0 8px 18px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(58,58,58,0.86)' : 'rgba(255,255,255,0.92)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const Title = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.8125rem;
  }
`;

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.dm.listPad};

  /* 사이드바와 동일한 스크롤바 숨김 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.dmMobile.listPad};
  }
`;

/* ---- 섹션 헤더 ---- */

const SectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  &:hover { color: ${({ theme }) => theme.colors.textSecondary}; }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 8px 8px 4px;
    font-size: 0.6875rem;
  }
`;

const SectionTitle = styled.span``;

/* ---- 앱 아이템 ---- */

const AppItem = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.dm.listItemGap};
  padding: ${({ theme }) => theme.dm.listItemPad};
  border: none;
  border-radius: ${({ theme }) => theme.dm.listItemRadius};
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : '#ffffff'
      : 'transparent'};
  box-shadow: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? '0 1px 4px rgba(0,0,0,0.3), 0 0 0 0.5px rgba(255,255,255,0.06)'
        : '0 1px 4px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)'
      : 'none'};
  cursor: pointer;
  transition: background 0.15s ease-out, box-shadow 0.15s ease-out;
  text-align: left;
  &:hover {
    background: ${({ $active, theme }) =>
      $active
        ? theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff'
        : theme.colors.bgHover};
  }
`;

const AppLogoWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: ${({ theme }) => theme.dm.avatarSize};
  height: ${({ theme }) => theme.dm.avatarSize};
  border-radius: 22%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
`;

const AppAvatarImg = styled.img`
  width: 76%;
  height: 76%;
  object-fit: contain;
  display: block;
`;


const AppItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AppItemName = styled.div`
  font-size: ${({ theme }) => theme.dm.nameFontSize};
  font-weight: ${({ theme }) => theme.dm.nameWeight};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AppItemDesc = styled.div`
  font-size: ${({ theme }) => theme.dm.previewFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ---- 팀 대화 아이템 ---- */

const ContactItem = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.dm.listItemGap};
  padding: ${({ theme }) => theme.dm.listItemPad};
  border: none;
  border-radius: ${({ theme }) => theme.dm.listItemRadius};
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : '#ffffff'
      : 'transparent'};
  box-shadow: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? '0 1px 4px rgba(0,0,0,0.3), 0 0 0 0.5px rgba(255,255,255,0.06)'
        : '0 1px 4px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)'
      : 'none'};
  cursor: pointer;
  transition: background 0.15s ease-out, box-shadow 0.15s ease-out;
  text-align: left;
  &:hover {
    background: ${({ $active, theme }) =>
      $active
        ? theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff'
        : theme.colors.bgHover};
  }
`;

const ContactAvatar = styled.div<{ $color: string }>`
  flex-shrink: 0;
  width: ${({ theme }) => theme.dm.avatarSize};
  height: ${({ theme }) => theme.dm.avatarSize};
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e'};
  font-weight: ${({ theme }) => theme.dm.avatarFontWeight};
  font-size: ${({ theme }) => theme.dm.avatarFontSize};
  user-select: none;
  overflow: hidden;
`;

const ContactAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

const ContactInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ContactName = styled.div`
  font-size: ${({ theme }) => theme.dm.nameFontSize};
  font-weight: ${({ theme }) => theme.dm.nameWeight};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContactPreview = styled.div`
  font-size: ${({ theme }) => theme.dm.previewFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UnreadBadge = styled.div`
  min-width: ${({ theme }) => theme.dm.badgeSize};
  height: ${({ theme }) => theme.dm.badgeSize};
  padding: 0 5px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.error};
  color: #fff;
  font-size: ${({ theme }) => theme.dm.badgeFontSize};
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

